/**
 * FlowchartAgent.js
 * Main page for generating and editing flowcharts using AI
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTeam } from "../context/TeamContext";
import DataService from "../services/DataService";
import MermaidRenderer from "../components/MermaidRenderer";
import MarkdownViewer from "../components/MarkdownViewer";
import { FaCheckSquare, FaSquare, FaPlay, FaArrowLeft } from "react-icons/fa";

export default function FlowchartAgent() {
  const { executionId } = useParams();
  const navigate = useNavigate();
  const { currentTeam, permissions } = useTeam();

  const [contexts, setContexts] = useState([]);
  const [selectedContexts, setSelectedContexts] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [numProcesses, setNumProcesses] = useState("auto");
  const [aiProvider, setAiProvider] = useState("openai"); // google or openai
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentExecution, setCurrentExecution] = useState(null);
  const [activeTab, setActiveTab] = useState("graphs");

  const dataService = new DataService();

  useEffect(() => {
    if (currentTeam) {
      loadContexts();
    }
  }, [currentTeam]);

  useEffect(() => {
    if (executionId && currentTeam) {
      loadExecution();
    }
  }, [executionId, currentTeam]);

  const loadContexts = async () => {
    try {
      const data = await dataService.getContexts(currentTeam.id);
      const readyContexts = data.filter((c) => c.status === "ready");
      setContexts(readyContexts);
    } catch (error) {
      console.error("Error loading contexts:", error);
    }
  };

  const loadExecution = async () => {
    try {
      setLoading(true);
      const execution = await dataService.getAgentExecution(
        currentTeam.id,
        executionId
      );
      if (execution) {
        setCurrentExecution(execution);
        setResult(execution.result);
        setPrompt(execution.prompt || "");
        setSelectedContexts(execution.contextIds || []);
      }
    } catch (error) {
      console.error("Error loading execution:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleContext = (contextId) => {
    setSelectedContexts((prev) =>
      prev.includes(contextId)
        ? prev.filter((id) => id !== contextId)
        : [...prev, contextId]
    );
  };

  const handleExecute = async () => {
    if (selectedContexts.length === 0) {
      alert("Please select at least one context document");
      return;
    }

    try {
      setExecuting(true);

      const response = await dataService.executeFlowchartAgent(
        selectedContexts,
        prompt,
        {
          temperature: 0.7,
          maxTokens: 65535,
          diagramType: "auto",
          numProcesses: numProcesses,
          aiProvider: aiProvider,
        },
        currentTeam.id
      );

      if (response.success) {
        setResult(response.data);

        // Navigate to the execution page
        if (response.data.executionId) {
          navigate(`/data-next/agents/flowchart/${response.data.executionId}`);
        }
      } else {
        alert("Failed to generate flowchart: " + response.error);
      }
    } catch (error) {
      console.error("Error executing agent:", error);
      alert("Error generating flowchart: " + error.message);
    } finally {
      setExecuting(false);
    }
  };

  const handleSave = async (newCode) => {
    if (!currentExecution) return;

    try {
      await dataService.saveExecutionVersion(
        currentTeam.id,
        currentExecution.executionId,
        newCode,
        "Manual edit"
      );
      alert("Changes saved!");
    } catch (error) {
      console.error("Save error:", error);
      alert("Failed to save changes");
    }
  };

  const handleCodeChange = (newCode) => {
    setResult((prev) => ({
      ...prev,
      mermaidCode: newCode,
    }));
  };

  if (!permissions.canWrite) {
    return (
      <div className="flowchart-agent-page">
        <div className="access-denied-message">
          <h2>Access Denied</h2>
          <p>You need creator or owner permissions to generate flowcharts.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Loading execution...</p>
      </div>
    );
  }

  return (
    <div className="flowchart-agent-page">
      {/* Header */}
      <div className="page-header">
        <button
          onClick={() => navigate("/data-next/agents")}
          className="btn btn-icon back-button"
        >
          <FaArrowLeft /> Back to Agents
        </button>
        <div>
          <h1>Flowchart Generator</h1>
          <p className="page-subtitle">
            Generate AI-powered Mermaid diagrams from your context
          </p>
        </div>
      </div>

      {/* Workflow Steps */}
      <div className="workflow-container">
        {/* Step 1: Select Contexts */}
        {!result && (
          <>
            <div className="workflow-step">
              <div className="step-header">
                <div className="step-number">1</div>
                <h2>Select Context Documents</h2>
              </div>
              <div className="step-content">
                {contexts.length > 0 ? (
                  <>
                    <div className="context-selection-header">
                      <span className="selection-count">
                        {selectedContexts.length} of {contexts.length} selected
                      </span>
                      <button
                        onClick={() =>
                          setSelectedContexts(contexts.map((c) => c.id))
                        }
                        className="btn btn-sm btn-secondary"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => setSelectedContexts([])}
                        className="btn btn-sm btn-secondary"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="context-list">
                      {contexts.map((ctx) => (
                        <label
                          key={ctx.id}
                          className={`context-checkbox-item ${
                            selectedContexts.includes(ctx.id) ? "selected" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedContexts.includes(ctx.id)}
                            onChange={() => toggleContext(ctx.id)}
                          />
                          {selectedContexts.includes(ctx.id) ? (
                            <FaCheckSquare />
                          ) : (
                            <FaSquare />
                          )}
                          <div className="context-item-info">
                            <span className="context-item-name">
                              {ctx.name}
                            </span>
                            <span className="context-item-type">
                              {ctx.type}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="empty-state-small">
                    <p>No context documents available.</p>
                    <button
                      onClick={() => navigate("/data-next/context-library")}
                      className="btn btn-primary"
                    >
                      Upload Contexts
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: Configure */}
            <div className="workflow-step">
              <div className="step-header">
                <div className="step-number">2</div>
                <h2>Configure Your Diagram</h2>
              </div>
              <div className="step-content">
                <div className="form-group">
                  <label>What do you want to visualize?</label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Example: Create flowcharts for the hiring process described in the interview transcript"
                    className="prompt-textarea"
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>
                    How many processes/workflows are in your context?
                  </label>
                  <select
                    value={numProcesses}
                    onChange={(e) => setNumProcesses(e.target.value)}
                    className="select"
                  >
                    <option value="auto">Auto-detect (AI decides)</option>
                    <option value="1">Single process (simple flowchart)</option>
                    <option value="2">Two processes (use subgraphs)</option>
                    <option value="3">Three processes (use subgraphs)</option>
                    <option value="multiple">
                      Multiple processes (AI decides count)
                    </option>
                  </select>
                  <p className="form-hint">
                    💡 Select "Auto-detect" to let AI decide, or specify the
                    exact number for better results. For example, if your
                    document describes "New Hire" and "Rehire" processes, select
                    "Two processes".
                  </p>
                </div>

                <div className="form-group">
                  <label>AI Provider</label>
                  <select
                    value={aiProvider}
                    onChange={(e) => setAiProvider(e.target.value)}
                    className="select"
                  >
                    <option value="openai">OpenAI GPT-5 (Azure)</option>
                    <option value="google">Google Gemini 2.5 Pro</option>
                  </select>
                  <p className="form-hint">
                    💡 Choose which AI model to use for diagram generation.
                    Google Gemini is faster, OpenAI GPT-5 has larger context
                    window.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3: Execute */}
            <div className="workflow-step">
              <div className="step-header">
                <div className="step-number">3</div>
                <h2>Generate Flowchart</h2>
              </div>
              <div className="step-content">
                <button
                  onClick={handleExecute}
                  disabled={executing || selectedContexts.length === 0}
                  className="btn btn-primary btn-lg execute-button"
                >
                  {executing ? (
                    <>
                      <div className="spinner-sm"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <FaPlay /> Generate Flowchart
                    </>
                  )}
                </button>
                {executing && (
                  <p className="executing-message">
                    This may take 10-30 seconds. AI is analyzing your
                    contexts...
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Result Display */}
        {result && result.mermaidCode && (
          <div className="result-section">
            <div className="result-header">
              <h2>Generated Result</h2>
              <div className="result-actions">
                <button
                  onClick={() => {
                    setResult(null);
                    setPrompt("");
                    setActiveTab("graphs");
                  }}
                  className="btn btn-secondary"
                >
                  Generate New Diagram
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="result-tabs">
              <button
                className={`tab-button ${
                  activeTab === "graphs" ? "active" : ""
                }`}
                onClick={() => setActiveTab("graphs")}
              >
                📊 Flowcharts
              </button>
              <button
                className={`tab-button ${activeTab === "docs" ? "active" : ""}`}
                onClick={() => setActiveTab("docs")}
              >
                📄 Documentation
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === "graphs" ? (
              <div className="tab-content">
                <p className="tab-hint">
                  💡 Click "Show Editor" to edit code • Use "View Fullscreen"
                  for individual diagrams
                </p>
                <MermaidRenderer
                  code={result.mermaidCode}
                  editable={true}
                  onChange={handleCodeChange}
                  onSave={currentExecution ? handleSave : null}
                  title={
                    currentExecution
                      ? `Execution ${currentExecution.executionId}`
                      : "New Flowchart"
                  }
                />
              </div>
            ) : (
              <div className="tab-content">
                <MarkdownViewer
                  markdown={
                    result.markdownDocs ||
                    "# Documentation\n\nNo documentation available."
                  }
                  title={
                    currentExecution
                      ? currentExecution.executionId
                      : "Process Documentation"
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
